import { useEffect } from 'react';
import { useSettings } from '@/hooks/useProducts';
import { refreshWidgets } from '@/lib/integrations/storefront-events';

interface YotpoPublic {
  app_key: string;
  hide_native: boolean;
  star_rating: boolean;
}

/** Yotpo's public storefront config, or null when the connector is not connected. */
export function useYotpoPublic(): YotpoPublic | null {
  const { data } = useSettings();
  const cfg = data?.settings?.integrations_public?.yotpo as Record<string, string> | undefined;
  if (!cfg?.app_key) return null;
  return { app_key: cfg.app_key, hide_native: cfg.hide_native === '1', star_rating: cfg.star_rating === '1' };
}

interface ProductLike {
  id: string;
  name: string;
  slug?: string | null;
  price?: number | null;
  base_price?: number | null;
  image_url?: string | null;
  images?: Array<{ url: string }>;
  variants?: Array<{ price?: number | null }>;
}

/** Main reviews widget for a product page. Renders nothing unless Yotpo is connected. */
export default function YotpoReviewsWidget({ product, currency = 'USD' }: { product: ProductLike; currency?: string }) {
  const yotpo = useYotpoPublic();
  useEffect(() => {
    if (yotpo) refreshWidgets();
  }, [yotpo?.app_key, product.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!yotpo) return null;
  const price = product.variants?.[0]?.price ?? product.price ?? product.base_price ?? 0;
  const image = product.images?.[0]?.url ?? product.image_url ?? '';
  const url = `${window.location.origin}/product/${product.slug ?? product.id}`;
  return (
    <div
      className="yotpo yotpo-main-widget"
      data-testid="yotpo-main-widget"
      data-product-id={product.id}
      data-price={String(price)}
      data-currency={currency}
      data-name={product.name}
      data-url={url}
      data-image-url={image}
    />
  );
}

/** Star-rating "bottom line" for product pages/cards. */
export function YotpoStarRating({ productId, className }: { productId: string; className?: string }) {
  const yotpo = useYotpoPublic();
  useEffect(() => {
    if (yotpo) refreshWidgets();
  }, [yotpo?.app_key, productId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!yotpo || !yotpo.star_rating) return null;
  return <div className={`yotpo bottomLine ${className ?? ''}`.trim()} data-testid="yotpo-bottomline" data-product-id={productId} />;
}
